import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import Tooltip from "../../../components/form-components/Tooltip";
import { getSourceValues } from "../api";

const euro = (n) => `€${Math.round(n || 0).toLocaleString()}`;

// Pluralise the facet noun based on count. Avoids "Showing 1 projects" and incorrect plurals
// like "sku_categorys" — finance-grade tools shouldn't ship bad grammar.
const SINGULAR = { project: "project", label: "label", sku_category: "SKU category", service: "service" };
const PLURAL = { project: "projects", label: "labels", sku_category: "SKU categories", service: "services" };
const pluralise = (facet, count) => {
  const dict = count === 1 ? SINGULAR : PLURAL;
  return dict[facet] || (facet.replace("_", " ") + (count === 1 ? "" : "s"));
};

const ValuePicker = ({ facet, labelKey, selected, onToggle, initialSearch, sourceTypeId, excludeSourceId }) => {
  const [search, setSearch] = useState(initialSearch || "");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const needsLabelKey = facet === "label" && !labelKey;

  const { data, isLoading, error } = useQuery(
    ["source-values", facet, labelKey, debounced, sourceTypeId, excludeSourceId ?? null],
    () => getSourceValues({ groupBy: facet, labelKey, search: debounced, limit: 100, source_type_id: sourceTypeId, excludeSourceId }),
    { enabled: !needsLabelKey, keepPreviousData: true }
  );

  const values = data?.values || [];

  return (
    <div>
      {/* Search field — matches the report editor sidebar's filter-values input:
          magnifying-glass icon on the left, rounded pill, light background, sharp focus. */}
      <div className="relative mb-2">
        <FontAwesomeIcon
          icon={icon({ name: "magnifying-glass", family: "sharp", style: "light" })}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3 text-sm pointer-events-none"
        />
        <input
          type="text"
          aria-label="search values"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${facet === "project" ? "projects" : facet.replace("_", " ") + "s"}…`}
          className="w-full pl-9 pr-3 py-2 text-text-2 text-sm border border-border rounded-ctrl focus:outline-none focus:border-primary bg-surface-2"
        />
      </div>
      {needsLabelKey && (
        <p className="text-xs text-text-2 italic">Choose a label key first.</p>
      )}
      {!needsLabelKey && isLoading && (
        <p className="text-xs text-text-2">Loading…</p>
      )}
      {!needsLabelKey && error && (
        <p className="text-xs text-danger">Couldn't load values. Try again.</p>
      )}
      {!needsLabelKey && !isLoading && !error && values.length === 0 && (
        <p className="text-xs text-text-2 italic">
          No {facet.replace("_", " ")} found
          {debounced ? ` for "${debounced}"` : " in the last 30 days"}.
        </p>
      )}
      {/* Total-count hint — tells the user how many results are showing so they know
          whether to search to filter further (or to find values not in the top 100). */}
      {!needsLabelKey && !isLoading && !error && values.length > 0 && !debounced && (
        <p className="text-[11px] text-text-3 mb-1.5">
          {values.length >= 100
            ? "Showing top 100 — search to find others"
            : `Showing ${values.length} ${pluralise(facet, values.length)}`}
        </p>
      )}
      {values.length > 0 && (
        <div className="border border-border rounded-card overflow-hidden max-h-64 overflow-y-auto">
          {values.map((v) => {
            const isSel = selected.includes(v.value);
            const displayLabel = v.label || v.value;
            // Raw value differs from the friendly label (UUID, opaque key, …). Surface it on
            // hover as a native browser tooltip instead of taking up a whole second line — the
            // ids are engineering noise for the finance reader.
            const hasRawSuffix = v.label && v.label !== v.value;
            const trendPct = v.trendPct;
            const trendUp = trendPct != null && trendPct >= 0;
            const trendAbs = trendPct != null ? Math.round(Math.abs(trendPct) * 100) : null;

            return (
              <div
                key={v.value}
                className={`group w-full flex items-center gap-2.5 px-3 py-2 border-b border-border last:border-b-0 ${
                  isSel ? "bg-primary-soft" : "hover:bg-surface-2"
                }`}
              >
                {/* Toggle button: checkbox + label text. */}
                <button
                  type="button"
                  onClick={() => onToggle(v.value)}
                  className="flex items-center gap-2.5 flex-1 min-w-0 text-left"
                >
                  {isSel && (
                    <span
                      data-testid="value-check"
                      className="flex-none rounded-full bg-primary flex items-center justify-center"
                      style={{ width: 16, height: 16 }}
                      aria-hidden
                    >
                      <FontAwesomeIcon icon={icon({ name: "check", style: "solid" })} className="text-white text-xs" />
                    </span>
                  )}
                  <span className="flex-1 min-w-0">
                    <span
                      className="block text-sm text-text truncate"
                      title={hasRawSuffix ? v.value : undefined}
                    >
                      {displayLabel}
                    </span>
                  </span>
                </button>

                {/* Env chip */}
                {v.env && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-2 text-text-2 flex-none">
                    {v.env}
                  </span>
                )}

                {/* Owner chip — full text on hover via title. Wider max-width so "Unallocated /
                    Shared Services" reads without truncating mid-word for the common cases. */}
                {v.owners?.length > 0 && (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-2 text-text-2 truncate max-w-[200px] flex-none"
                    title={v.owners.join(", ")}
                  >
                    {v.owners[0]}{v.owners.length > 1 ? ` +${v.owners.length - 1}` : ""}
                  </span>
                )}

                {/* Cost trend badge — uses the shared instantTooltip portal (fast, styled,
                    reliable) instead of native title, so the "vs prior 30 days" comparison
                    frame actually shows up on hover. */}
                {trendPct != null && (
                  <span
                    data-tooltip-id="instantTooltip"
                    data-tooltip-header="Trend"
                    data-tooltip-content="Compared to the prior 30 days. ▲ red = cost going up; ▼ teal = cost going down."
                    className={`text-[11px] font-medium flex-none cursor-help ${trendUp ? "text-danger" : "text-success"}`}
                  >
                    {trendUp ? "▲" : "▼"} {trendAbs}%
                  </span>
                )}

                {/* Cost */}
                <span className="text-sm font-medium text-text flex-none">
                  {euro(v.monthlyCost)}
                  <span className="text-xs text-text-3">/mo</span>
                </span>

                {/* Deselect affordance — visible × icon that appears on row hover for selected
                    rows. Direct visual affordance beats a tooltip: the user knows what will
                    happen without needing to hover and read. */}
                {isSel && (
                  <button
                    type="button"
                    aria-label={`Deselect ${displayLabel}`}
                    onClick={(e) => { e.stopPropagation(); onToggle(v.value); }}
                    className="flex-none w-5 h-5 rounded-full flex items-center justify-center text-text-2 hover:text-text hover:bg-card opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  >
                    <FontAwesomeIcon icon={icon({ name: "xmark", style: "solid" })} className="text-xs" />
                  </button>
                )}
              </div>
            );
          })}
          <Tooltip />
        </div>
      )}
    </div>
  );
};

ValuePicker.propTypes = {
  facet: PropTypes.string.isRequired,
  labelKey: PropTypes.string,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  initialSearch: PropTypes.string,
  sourceTypeId: PropTypes.string,
  excludeSourceId: PropTypes.number,
};

export default ValuePicker;
